import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PortalShell from "@/components/portal/PortalShell";
import { DocUploader } from "@/components/portal/DocUploader";
import { Loader2, CheckCircle2, ShieldCheck, FileText, MessageSquare } from "lucide-react";

const help = (
  <a href="mailto:ops@connex.is" className="rounded-full border border-white/15 hover:border-white/30 px-3.5 h-9 inline-flex items-center text-white transition-colors" data-testid="case-chase-help">
    Need a hand?
  </a>
);

/** One private page for everything the lender still needs — answers and documents, no login, phone-first. */
export default function ClientCaseChase() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => api.get(`/apply/case/${token}`).then((r) => setData(r.data))
    .catch((e) => setErr(e?.response?.data?.detail || "That link isn't valid — reply to our message and we'll send a fresh one"));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data && !err) return <div className="min-h-screen bg-[#0B1220] grid place-items-center"><Loader2 className="size-6 animate-spin text-[#7da9ff]" /></div>;
  if (err) {
    return (
      <PortalShell variant="apply" kind="introducer" ctas={help} eyebrow="Connexis · Your application" title="We couldn't open that link">
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 max-w-xl text-[14px] text-slate-300" data-testid="case-chase-error">{err}</div>
      </PortalShell>
    );
  }

  const open = data.questions.filter((q) => !q.answered).length + data.docs.filter((d) => !d.received).length;
  return (
    <PortalShell variant="apply" kind="introducer" ctas={help} eyebrow="Connexis · Your application"
                 title={open ? "A couple of things and you're done" : "Thank you — that's everything"}
                 subtitle={`For ${data.company_name || "your business"}${data.reference ? ` · ${data.reference}` : ""}`}>
      <div className="max-w-2xl space-y-4" data-testid="case-chase-page">
        {data.questions.map((q) => <Question key={q.token} q={q} onDone={load} />)}
        {data.docs.map((d) => <Docs key={d.token} d={d} />)}
        <div className="flex items-start gap-2.5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-[12.5px] text-slate-400">
          <ShieldCheck className="size-4 text-[#7da9ff] mt-0.5 shrink-0" />
          This page is private to your application and only shared with the lender considering it. We will never ask
          for passwords, PINs or a payment.
        </div>
      </div>
    </PortalShell>
  );
}

const Done = ({ children, testId }) => (
  <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-[12.5px] text-emerald-900" data-testid={testId}>
    <CheckCircle2 className="size-4 text-emerald-600 mt-0.5 shrink-0" />{children}
  </div>
);

function Question({ q, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/apply/question/${q.token}/answer`, { answer: text.trim() }); onDone(); }
    catch { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`case-chase-question-${q.token}`}>
      <div className="flex items-start gap-2 text-[14px] font-semibold text-slate-900">
        <MessageSquare className="size-4 text-[#0055FF] mt-0.5 shrink-0" />{q.question}
      </div>
      {q.answered ? <Done testId={`case-chase-answered-${q.token}`}>Answered: {q.answer}</Done> : (
        <>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Type your answer"
                    className="mt-3 w-full rounded-xl border border-slate-300 p-3 text-[14px] text-slate-900 focus:border-[#0055FF] focus:outline-none"
                    data-testid={`case-chase-answer-${q.token}`} />
          <button onClick={send} disabled={busy || !text.trim()}
                  className="mt-2 inline-flex h-10 items-center rounded-full bg-[#0055FF] px-5 text-[13px] font-semibold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-50"
                  data-testid={`case-chase-answer-send-${q.token}`}>
            {busy ? "Sending…" : "Send answer"}
          </button>
        </>
      )}
    </div>
  );
}

function Docs({ d }) {
  const [files, setFiles] = useState([]);
  const got = d.received || files.length > 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`case-chase-docs-${d.token}`}>
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-600">Documents needed</div>
      <ul className="mt-2 space-y-1.5">
        {d.items.map((i) => <li key={i} className="flex items-start gap-2 text-[13.5px] text-slate-800"><FileText className="size-3.5 text-slate-400 mt-1 shrink-0" />{i}</li>)}
      </ul>
      {d.note && <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12.5px] text-slate-700">{d.note}</div>}
      {got && <Done testId={`case-chase-received-${d.token}`}>Received — thank you. Add more below if you have them.</Done>}
      <div className="mt-4">
        <DocUploader files={files} onChange={setFiles} max={12} allowRemove={false}
                     endpoint={`/apply/docs/${d.token}/upload`} testId={`case-chase-uploader-${d.token}`} />
      </div>
      <p className="mt-2 text-[12px] text-slate-500">A photo of each document on your phone is fine.</p>
    </div>
  );
}
